/**
 * Structure action for transcribed audio blocks (Phase 3 of the local-first
 * audio pipeline): annotation applogs → `@wovin/function-structure-transcript`
 * (JSR; same canonical step the daemon runs) → structured note tree as
 * children of the recording block, with `structuredTranscript/*` provenance.
 *
 * The package's `OutputThreadLike` sink is satisfied by a thin wrapper over
 * note3's current thread; `push` is a no-op because the sync service handles
 * publishing. Idempotency comes from `isAlreadyProcessed` — deleting a
 * structured tree tombstones its applogs out of the current thread, which
 * naturally re-enables the action.
 */

import type { EntityID } from '@wovin/core/applog'
import type { ThreadOnlyCurrentNoDeleted } from '@wovin/core/thread'
import type { AnnotationSegment, OutputThreadLike, TranscriptEntity } from '@wovin/function-structure-transcript'
import { queryAndMap } from '@wovin/core'
import { isAlreadyProcessed, processEntity, renderPrompt } from '@wovin/function-structure-transcript'
import { Logger } from 'besonders-logger'
import { createSignal } from 'solid-js'
import { useAgent } from '../data/agent/AgentState'
import { insertApplogs } from '../data/ApplogDB'
import { getLazyHashTags, getLazyPlusTags } from '../ui/reactive'
import { effectiveStructureModel, requireMistralApiKey } from './ai-settings'

const { LOG, WARN, DEBUG } = Logger.setup(Logger.DEBUG, { prefix: '[structure]' })

/** Cap for tag/project lists injected into the prompt (sorted by usage count). */
const PROMPT_LIST_LIMIT = 50

const [structuringBlocks, setStructuringBlocks] = createSignal<ReadonlySet<EntityID>>(new Set())
export { structuringBlocks }
export const isStructuring = (blockID: EntityID) => structuringBlocks().has(blockID)

function markStructuring(blockID: EntityID, active: boolean) {
	setStructuringBlocks((prev) => {
		const next = new Set(prev)
		if (active) next.add(blockID)
		else next.delete(blockID)
		return next
	})
}

export class AlreadyStructuredError extends Error {
	constructor() {
		super('This transcript was already structured — delete the previous structured notes to re-run')
	}
}

interface CollectedTranscript {
	/** Transcription (metadata) entity — provenance anchor; the audio block ID when absent. */
	trxId: EntityID
	annotations: AnnotationSegment[]
}

/** Gather the newest transcription's annotation segments for an audio block. */
export function collectTranscript(blockID: EntityID, thread: ThreadOnlyCurrentNoDeleted): CollectedTranscript | null {
	const annIds = queryAndMap(thread, [{ en: '?ann', at: 'annotation/recording', vl: blockID }], 'ann') as EntityID[]
	if (!annIds.length) return null

	// Newest transcription run wins (applog order); annotations link back via sourceTranscript.
	const trxIds = queryAndMap(thread, [{ en: '?trx', at: 'transcription/recording', vl: blockID }], 'trx') as EntityID[]
	const trxId = trxIds.at(-1) ?? blockID

	const annotations: AnnotationSegment[] = []
	for (const annId of annIds) {
		const attr = (at: string) => queryAndMap(thread, [{ en: annId, at, vl: '?v' }], 'v')[0]
		if (trxIds.length && attr('annotation/sourceTranscript') !== trxId) continue
		const startMs = attr('annotation/startMs')
		const endMs = attr('annotation/endMs')
		const transcript = attr('annotation/transcript')
		if (startMs == null || endMs == null || typeof transcript !== 'string') continue
		const diarization = attr('annotation/diarization')
		annotations.push({
			startMs: Number(startMs),
			endMs: Number(endMs),
			transcript,
			...(diarization != null ? { diarization: String(diarization) } : {}),
		})
	}
	if (!annotations.length) return null
	annotations.sort((a, b) => a.startMs - b.startMs)
	return { trxId, annotations }
}

function topTagNames(tags: Map<string, number>): string[] {
	return [...tags.entries()]
		.sort((a, b) => b[1] - a[1])
		.slice(0, PROMPT_LIST_LIMIT)
		.map(([name]) => name)
}

/**
 * Structure an audio block's transcript into child notes.
 * Throws `AlreadyStructuredError` when provenance for this transcription exists.
 */
export async function structureAudioBlock(blockID: EntityID, thread: ThreadOnlyCurrentNoDeleted): Promise<void> {
	if (isStructuring(blockID)) {
		WARN('Structuring already running for block', blockID)
		return
	}
	const apiKey = requireMistralApiKey()
	const collected = collectTranscript(blockID, thread)
	if (!collected) throw new Error('No transcript found — run Transcribe first')
	const { trxId, annotations } = collected

	const agent = useAgent()
	const outputThread: OutputThreadLike = {
		// exposes .applogs; current-thread filters make deleted outputs invisible (see
		// module doc). Cast bridges the package's pinned @wovin/core@0.1.33 Applog type
		// vs the workspace one — structurally the same plain objects at runtime.
		thread: thread as unknown as OutputThreadLike['thread'],
		agentHash: agent.ag,
		insert: applogs => insertApplogs(thread, applogs),
		push: async () => {}, // sync service publishes on its own schedule
	}

	if (isAlreadyProcessed(trxId, outputThread)) throw new AlreadyStructuredError()

	// The daemon curates projects.txt/tags.txt by hand — in note3 we inject the
	// thread's own plus/hash tags (by usage count) instead.
	const prompt = renderPrompt({
		projects: topTagNames(getLazyPlusTags(thread)),
		tags: topTagNames(getLazyHashTags(thread)),
	})

	// audioEntity is deliberately NOT set: it triggers the daemon's upstream-routing
	// re-publish (incl. a relation/replyTo applog on the audio block, self-referencing
	// here). The recording link already exists via transcription/recording.
	const entity: TranscriptEntity = {
		entityId: trxId,
		text: annotations.map(a => a.transcript).join('\n'),
		sourceEntity: trxId,
		replyTo: blockID, // parents the structured tree under the recording block
		annotations,
	}

	markStructuring(blockID, true)
	try {
		const model = effectiveStructureModel()
		LOG('Structuring transcript', trxId, 'of block', blockID, { model, segments: annotations.length })
		await processEntity(entity, {
			outputThread,
			mistralApiKey: apiKey,
			mistralModel: model,
			sourceIpns: agent.w3NamePublic ?? 'note3-local',
			saveArtifacts: async artifacts => DEBUG('LLM call artifacts', artifacts),
		}, prompt)
		LOG('Structured transcript for block', blockID)
	} finally {
		markStructuring(blockID, false)
	}
}
